import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"
import { createLLMOMeta, createBreadcrumbSchema, createToolSchema, createHowToSchema, createFAQSchema } from "@/lib/seo"
import { getToolBySlug } from "@/data/tools/tools-registry"
import ToolPageLayout from "@/components/tools/ToolPageLayout"
import { SelectField, SubmitButton, ResetButton } from "@/components/tools/ToolFormField"
import { SectionHeading, Paragraph, FeatureGrid, TipList, ContentSection } from "@/components/tools/SeoContent"

const tool = getToolBySlug("instagram-hashtag-generator")!

export const Route = createFileRoute("/tools/instagram-hashtag-generator")({
  head: () => ({
    meta: [
      ...createLLMOMeta({
        title: tool.seo.title,
        description: tool.seo.description,
        url: "https://zapify.pro/tools/instagram-hashtag-generator",
      }),
      { name: "keywords", content: tool.seo.keywords.join(", ") },
    ],
    links: [{ rel: "canonical", href: "https://zapify.pro/tools/instagram-hashtag-generator" }],
    scripts: [
      createToolSchema({ name: tool.name, description: tool.seo.description, url: "https://zapify.pro/tools/instagram-hashtag-generator" }),
      createBreadcrumbSchema({ itemListElement: [
        { position: 1, name: "Home", item: "https://zapify.pro" },
        { position: 2, name: "Free Tools", item: "https://zapify.pro/tools" },
        { position: 3, name: tool.shortName, item: "https://zapify.pro/tools/instagram-hashtag-generator" },
      ]}),
      createHowToSchema({ name: "How to Generate Instagram Hashtags", description: tool.seo.description, steps: tool.howToSteps }),
      createFAQSchema(tool.faq),
    ],
  }),
  component: HashtagGeneratorPage,
})

// --- Curated hashtag database ---

const categoryHashtags: Record<string, string[]> = {
  fashion: ["#fashion", "#style", "#ootd", "#fashionblogger", "#outfitinspo", "#fashionstyle", "#streetstyle", "#fashionista", "#lookbook", "#whatiwore", "#fashiondiaries", "#styleinspo", "#outfitoftheday", "#fashiontrends", "#styleguide"],
  beauty: ["#beauty", "#makeup", "#skincare", "#beautytips", "#mua", "#beautyblogger", "#makeupartist", "#glam", "#beautyroutine", "#skincareroutine", "#makeuptutorial", "#beautyhacks", "#naturalbeauty", "#beautyreview", "#skincaretips"],
  travel: ["#travel", "#wanderlust", "#travelgram", "#travelphotography", "#explore", "#adventure", "#travelblogger", "#vacation", "#traveltheworld", "#instatravel", "#traveldiaries", "#bucketlist", "#traveltips", "#globetrotter", "#traveladdict"],
  food: ["#food", "#foodie", "#foodphotography", "#instafood", "#foodblogger", "#homemade", "#foodstagram", "#recipe", "#yummy", "#healthyfood", "#foodlover", "#cooking", "#eeeeeats", "#foodgasm", "#delicious"],
  fitness: ["#fitness", "#gym", "#workout", "#fitnessmotivation", "#fit", "#training", "#health", "#bodybuilding", "#fitfam", "#exercise", "#healthylifestyle", "#personaltrainer", "#fitnessjourney", "#gains", "#workoutmotivation"],
  lifestyle: ["#lifestyle", "#lifestyleblogger", "#dailylife", "#slowliving", "#lifestyleinspo", "#mindfulness", "#wellness", "#selfcare", "#dailyroutine", "#intentionalliving", "#lifestylephotography", "#goodvibes", "#livingwell", "#everydaymoments", "#simplepleasures"],
  business: ["#business", "#entrepreneur", "#smallbusiness", "#marketing", "#startup", "#businesstips", "#hustle", "#branding", "#success", "#businessowner", "#digitalmarketing", "#growthmindset", "#sidehustle", "#onlinebusiness", "#entrepreneurlife"],
  photography: ["#photography", "#photooftheday", "#photographer", "#photo", "#naturephotography", "#streetphotography", "#portrait", "#canon", "#shotoniphone", "#visualsoflife", "#photographylovers", "#ig_masterpiece", "#moodyfilms", "#earthportraits", "#goldenhour"],
  tech: ["#tech", "#technology", "#gadgets", "#innovation", "#coding", "#programming", "#ai", "#software", "#developer", "#techreview", "#appdevelopment", "#startuplife", "#machinelearning", "#techcommunity", "#futuretech"],
  art: ["#art", "#artist", "#artwork", "#drawing", "#painting", "#illustration", "#contemporaryart", "#sketch", "#artistsoninstagram", "#digitalart", "#creative", "#artoftheday", "#instaart", "#fineart", "#abstractart"],
  music: ["#music", "#musician", "#singer", "#songwriter", "#livemusic", "#newmusic", "#musicproducer", "#rap", "#hiphop", "#indie", "#guitar", "#musicislife", "#spotifyartist", "#beats", "#musicvideo"],
  parenting: ["#parenting", "#momlife", "#dadlife", "#parentingtips", "#motherhood", "#fatherhood", "#family", "#kids", "#babiesofinstagram", "#toddlerlife", "#parentingwin", "#newmom", "#familytime", "#raisingkids", "#parenthood"],
  pets: ["#pets", "#dogsofinstagram", "#catsofinstagram", "#puppy", "#kitten", "#petlovers", "#doglife", "#catlife", "#petsofinstagram", "#furbaby", "#doglover", "#catlover", "#adoptdontshop", "#petstagram", "#cutepets"],
  home: ["#homedecor", "#interiordesign", "#homedesign", "#decor", "#homestyle", "#interior", "#homeinspo", "#diy", "#organization", "#minimalistdesign", "#cozyhome", "#homemakeover", "#renovation", "#homegoals", "#apartmenttherapy"],
  education: ["#education", "#learning", "#studygram", "#teacher", "#onlinelearning", "#edtech", "#studytips", "#knowledge", "#learnontiktok", "#educationmatters", "#students", "#teachersofinstagram", "#elearning", "#studymotivation", "#lifelessons"],
  sports: ["#sports", "#athlete", "#training", "#gameday", "#sportsphotography", "#basketball", "#football", "#soccer", "#running", "#sportsmotivation", "#teamwork", "#champion", "#sportslife", "#fitathlete", "#competitivesports"],
  motivation: ["#motivation", "#inspiration", "#mindset", "#success", "#motivationalquotes", "#goals", "#believe", "#positivity", "#grind", "#nevergiveup", "#dailymotivation", "#successmindset", "#ambition", "#hustle", "#dreambig"],
}

const typeHashtags: Record<string, string[]> = {
  advice: ["#tips", "#advice", "#protips", "#didyouknow", "#lifehacks"],
  tutorial: ["#tutorial", "#howto", "#stepbystep", "#learnwithme", "#guide"],
  entertainment: ["#fun", "#entertainment", "#funny", "#trending", "#viral"],
  personal: ["#mystory", "#reallife", "#authentic", "#behindthescenes", "#dayinmylife"],
  promotion: ["#sale", "#deal", "#newlaunch", "#limitedoffer", "#shopnow"],
  inspiration: ["#inspiration", "#motivational", "#mondaymotivation", "#inspire", "#positivevibes"],
  review: ["#review", "#honest", "#productreview", "#unboxing", "#triedandtested"],
  news: ["#breaking", "#update", "#trending", "#news", "#whatsnew"],
}

const contentTypeOptions = [
  { value: "advice", label: "Advice & Tips" },
  { value: "tutorial", label: "Tutorial" },
  { value: "entertainment", label: "Entertainment" },
  { value: "personal", label: "Personal Story" },
  { value: "promotion", label: "Promotion" },
  { value: "inspiration", label: "Inspiration" },
  { value: "review", label: "Review" },
  { value: "news", label: "News" },
]

const categoryOptions = [
  { value: "fashion", label: "Fashion" },
  { value: "beauty", label: "Beauty" },
  { value: "travel", label: "Travel" },
  { value: "food", label: "Food" },
  { value: "fitness", label: "Fitness" },
  { value: "lifestyle", label: "Lifestyle" },
  { value: "business", label: "Business" },
  { value: "photography", label: "Photography" },
  { value: "tech", label: "Tech" },
  { value: "art", label: "Art" },
  { value: "music", label: "Music" },
  { value: "parenting", label: "Parenting" },
  { value: "pets", label: "Pets" },
  { value: "home", label: "Home" },
  { value: "education", label: "Education" },
  { value: "sports", label: "Sports" },
  { value: "motivation", label: "Motivation" },
]

function generateHashtags(contentType: string, category: string): string[] {
  const catTags = categoryHashtags[category] || []
  const typeTags = typeHashtags[contentType] || []
  const combined = [...catTags, ...typeTags]
  // Fisher-Yates shuffle for better randomness
  const shuffled = [...combined]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }
  return shuffled.slice(0, 10)
}

function HashtagGeneratorPage() {
  const [contentType, setContentType] = useState("")
  const [category, setCategory] = useState("")
  const [hashtags, setHashtags] = useState<string[] | null>(null)
  const [copied, setCopied] = useState(false)

  const handleGenerate = () => {
    if (!contentType || !category) return
    const result = generateHashtags(contentType, category)
    setHashtags(result)
    setCopied(false)
  }

  const handleGenerateMore = () => {
    if (!contentType || !category) return
    const result = generateHashtags(contentType, category)
    setHashtags(result)
    setCopied(false)
  }

  const copyAll = async () => {
    if (!hashtags) return
    const text = hashtags.join(" ")
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      const textarea = document.createElement("textarea")
      textarea.value = text
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand("copy")
      document.body.removeChild(textarea)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const copySingle = async (tag: string) => {
    try {
      await navigator.clipboard.writeText(tag)
    } catch {
      const textarea = document.createElement("textarea")
      textarea.value = tag
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand("copy")
      document.body.removeChild(textarea)
    }
  }

  const reset = () => {
    setContentType("")
    setCategory("")
    setHashtags(null)
    setCopied(false)
  }

  return (
    <ToolPageLayout
      tool={tool}
      content={
        <div className="space-y-10">
          <ContentSection>
            <SectionHeading>How Instagram Hashtags Work in 2026</SectionHeading>
            <Paragraph>
              Instagram's relationship with hashtags has undergone a fundamental transformation. In the early days of the platform, hashtags were the primary discovery mechanism — users would search hashtags, follow hashtags, and creators would load every post with 30 tags to maximize exposure. That era is over. In 2026, Instagram's algorithm has shifted decisively from <strong>hashtag-based discovery to content-based discovery</strong>, powered by machine learning models that analyze your image, video, audio, and caption text to determine who should see your post.
            </Paragraph>
            <Paragraph>
              This does not mean hashtags are dead — far from it. What has changed is their role. Hashtags now function primarily as <strong>context signals</strong> rather than discovery mechanisms. When you add a hashtag to your post, you are telling Instagram's classification system what your content is about, who it is for, and what conversations it belongs to. The algorithm uses this information alongside visual recognition, engagement patterns, and audience behavior to distribute your post to the right people. Think of hashtags as metadata that helps the algorithm understand your content faster and more accurately.
            </Paragraph>
            <Paragraph>
              The most significant shift is that <strong>quality now decisively outweighs quantity</strong>. Instagram's own Creators account and Head of Instagram Adam Mosseri have repeatedly confirmed that 3-5 highly targeted hashtags outperform 30 generic ones. The algorithm actively penalizes hashtag stuffing — using a wall of loosely related tags can actually reduce your reach because it sends mixed signals about what your content is about. When the algorithm cannot confidently classify your post, it shows it to fewer people.
            </Paragraph>
            <Paragraph>
              Another critical change is how Instagram evaluates <strong>hashtag-to-content coherence</strong>. The platform's AI analyzes whether the hashtags you use actually match the visual and textual content of your post. If you post a photo of latte art but tag it with #fitness and #workout, Instagram may reduce your reach across all hashtags on that post — not just the irrelevant ones. The system rewards precision and penalizes attempts to game distribution through unrelated trending tags. Creators who understand this shift and use hashtags strategically as content classifiers, not discovery hacks, consistently see better Explore page placement and higher reach per post.
            </Paragraph>
          </ContentSection>
          <ContentSection>
            <SectionHeading>Hashtag Best Practices</SectionHeading>
            <TipList tips={[
              { title: "Use 3-5 Hashtags Per Post (Not 30)", description: "The era of maxing out at 30 hashtags is behind us. Instagram's algorithm in 2026 responds best to a small, focused set of hashtags that clearly define your content. Using 3-5 highly relevant tags gives the algorithm a clear signal about your post's topic, audience, and context. When you use too many hashtags, you dilute that signal and force the algorithm to guess which audience segments to target. Start with 3-5 and test up to 8-10 if your engagement data supports it, but never exceed 15." },
              { title: "Mix Sizes: Niche, Mid-Volume, and Broad", description: "A smart hashtag strategy combines tags of different sizes. Niche hashtags (under 100K posts) give you the best chance of ranking in the top posts section. Mid-volume hashtags (100K-1M posts) balance competition with reach. Broad hashtags (1M+ posts) expose you to larger audiences but are extremely competitive. A typical mix might be 2 niche tags, 2 mid-volume tags, and 1 broad tag. This layered approach maximizes your chances of being discovered at multiple levels." },
              { title: "Avoid Banned and Shadowbanned Tags", description: "Instagram maintains a constantly evolving list of restricted hashtags that are associated with spam, inappropriate content, or community guideline violations. Using even one banned hashtag can suppress your entire post's reach — not just the reach from that tag, but from all tags on the post. Before using any hashtag, search for it on Instagram. If the results page shows a warning or displays no recent posts, the tag is likely restricted." },
              { title: "Rotate Hashtags Between Posts", description: "Using the exact same set of hashtags on every post is one of the fastest ways to trigger Instagram's spam detection. The algorithm interprets repetitive hashtag patterns as automated or low-effort behavior, which can result in reduced distribution. Create 5-10 different hashtag sets for your niche and rotate between them. Each set should be tailored to the specific content of that post." },
              { title: "Place Hashtags in the Caption or First Comment", description: "In 2026, there is no algorithmic difference between placing hashtags in your caption versus your first comment. Both methods are indexed identically by Instagram's system. The choice comes down to aesthetics and audience preference. Placing hashtags in the caption keeps everything in one place. Placing them in the first comment keeps your caption clean. Test both approaches and track which one your audience engages with more." },
            ]} />
          </ContentSection>
          <ContentSection>
            <SectionHeading>Types of Hashtags</SectionHeading>
            <FeatureGrid columns={3} items={[
              { icon: "\uD83C\uDFAF", title: "Niche Hashtags", description: "Highly specific to your exact topic, subtopic, or audience segment. Examples include #veganmealprep, #watercolorlandscapes, or #b2bsaasmarketing. Niche hashtags typically have fewer than 100K posts, which means less competition and a higher chance of your content appearing in the top posts section. Use niche hashtags as the foundation of every hashtag set." },
              { icon: "\uD83D\uDC65", title: "Community Hashtags", description: "Connect you to groups of people who share a common interest or identity. Examples include #bookstagram, #plantmom, or #fitfam. These tags are used by people who actively participate in a community and browse the hashtag feed to find new content. Community hashtags are powerful because the people using and following them are highly engaged." },
              { icon: "\uD83C\uDFF7\uFE0F", title: "Branded Hashtags", description: "Unique to your business or personal brand — tags you create and encourage your audience to use. Examples include #ShotOnPixel by Google or #MyCalvins by Calvin Klein. Creating a branded hashtag builds a searchable library of user-generated content around your brand. They create a sense of community and provide social proof." },
              { icon: "\uD83D\uDCCD", title: "Location Hashtags", description: "Location-based hashtags like #NYCfoodie, #LondonPhotography, or #TokyoTravel help you reach people interested in a specific geographic area. They are especially valuable for local businesses, travel creators, and event-based content. Location hashtags tend to have moderate competition and attract highly relevant audiences." },
              { icon: "\uD83D\uDCC8", title: "Trending Hashtags", description: "Tied to current events, viral moments, or seasonal topics. They offer a burst of short-term visibility but are extremely competitive. Use trending hashtags only when your content genuinely relates to the trend. When a trend aligns with your niche, act fast — trending hashtags have a window of 24-72 hours before they become oversaturated." },
            ]} />
          </ContentSection>
          <ContentSection>
            <SectionHeading>Hashtags to Avoid</SectionHeading>
            <Paragraph>
              Not all hashtags help your reach — some actively hurt it. <strong>Banned hashtags</strong> are the most dangerous. Instagram periodically restricts tags that have been associated with spam, bots, or guideline violations. Using even one banned tag can suppress your entire post's distribution. The tricky part is that banned hashtags often look perfectly innocent — tags like #adulting, #beautyblogger, or #dating have been restricted at various times. Always search a hashtag on Instagram before using it. If the results page shows a content advisory or displays no recent posts, do not use it.
            </Paragraph>
            <TipList tips={[
              { title: "Overly generic hashtags", description: "Tags like #love (2.1B+ posts), #instagood (1.8B+ posts), and #happy (700M+ posts) have so many posts that your content will be buried within seconds. Unless you already have a large, engaged following, these hashtags provide virtually zero discovery value. Your post competes with millions of others." },
              { title: "Irrelevant hashtags", description: "If your post is about cooking but you tag it with #fitness because you know that tag gets traffic, Instagram's AI will detect the mismatch. The result is reduced distribution across all your hashtags, not just the irrelevant one. Every hashtag on your post should directly describe the content, the audience, or the conversation your post belongs to." },
              { title: "Hashtags larger than your account can compete in", description: "If you have 500 followers, using a hashtag with 50 million posts means your content will never rank in the top posts for that tag. Focus on hashtags where the top-performing posts have similar engagement levels to yours. As your account grows, you can gradually move into larger, more competitive hashtags." },
            ]} />
          </ContentSection>
          <ContentSection>
            <SectionHeading>Measuring Hashtag Performance</SectionHeading>
            <Paragraph>
              Instagram provides direct data on hashtag performance through its built-in Insights feature. Navigate to any post's Insights and look for the <strong>"Reach from Hashtags"</strong> metric. This tells you exactly how many unique accounts discovered your post through the hashtags you used. Compare this number across posts with different hashtag sets to identify which combinations drive the most discovery. If a post's hashtag reach is below 5% of total reach, your hashtag strategy for that post underperformed.
            </Paragraph>
            <TipList tips={[
              { title: "Track performance systematically", description: "Create a simple spreadsheet where you log each post's hashtag set alongside its reach from hashtags, impressions, and engagement rate. Over 20-30 posts, clear patterns will emerge. You will find that certain hashtag combinations consistently outperform others. This data is far more valuable than any generic hashtag advice because it reflects your specific account, audience, and content style." },
              { title: "Apply the 80/20 rule", description: "Roughly 20% of your hashtags will drive 80% of your discovery. Once you identify your top-performing tags, build your core hashtag sets around them and experiment with the remaining slots. This approach gives you a reliable baseline of hashtag reach while still testing new tags for potential breakthroughs." },
              { title: "Retire underperforming hashtags", description: "If a hashtag has not contributed meaningful reach across 5-10 posts, replace it with a new option. Hashtag effectiveness changes over time as competition increases, Instagram's algorithm evolves, and audience behavior shifts. Review your hashtag performance monthly and refresh your sets quarterly. Treat your hashtag strategy as a living system that requires regular optimization." },
            ]} />
          </ContentSection>
        </div>
      }
    >
      <div className="max-w-2xl mx-auto">
        <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-6 sm:p-8 shadow-sm">
          {!hashtags ? (
            <div className="space-y-4">
              <SelectField
                label="Content Type"
                value={contentType}
                onChange={setContentType}
                options={contentTypeOptions}
                placeholder="Select content type..."
                required
              />
              <SelectField
                label="Category"
                value={category}
                onChange={setCategory}
                options={categoryOptions}
                placeholder="Select your niche..."
                required
              />
              <SubmitButton onClick={handleGenerate} disabled={!contentType || !category}>
                Generate Hashtags
              </SubmitButton>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Hashtag count */}
              <div className="text-center">
                <p className="text-sm font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1">
                  {hashtags.length} Hashtags Generated
                </p>
                <p className="text-xs text-neutral-400 dark:text-neutral-500">
                  {contentTypeOptions.find((o) => o.value === contentType)?.label} + {categoryOptions.find((o) => o.value === category)?.label}
                </p>
              </div>

              {/* Hashtag tags */}
              <div className="flex flex-wrap gap-2 justify-center">
                {hashtags.map((tag) => (
                  <button
                    key={tag}
                    onClick={() => copySingle(tag)}
                    title={`Copy ${tag}`}
                    className="px-3 py-1.5 rounded-full bg-brand-accent/10 dark:from-brand-accent/20 dark:via-brand-accent/20 dark:to-brand-accent/20 border border-brand-accent/20 dark:border-brand-accent/30 text-sm font-medium text-neutral-800 dark:text-neutral-200 hover:border-brand-accent/50 hover:shadow-sm transition-all cursor-pointer select-none"
                  >
                    {tag}
                  </button>
                ))}
              </div>

              {/* Copy all preview */}
              <div className="rounded-xl bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 p-4">
                <p className="text-sm text-neutral-600 dark:text-neutral-300 break-all font-mono leading-relaxed">
                  {hashtags.join(" ")}
                </p>
              </div>

              {/* Action buttons */}
              <div className="flex gap-3">
                <button
                  onClick={copyAll}
                  className={`flex-1 h-12 rounded-xl font-semibold text-sm transition-all duration-300 ${
                    copied
                      ? "bg-green-500 text-white"
                      : "bg-brand-accent text-white shadow-sm hover:shadow-md hover:-translate-y-0.5"
                  }`}
                >
                  {copied ? "Copied!" : "Copy All"}
                </button>
                <button
                  onClick={handleGenerateMore}
                  className="flex-1 h-12 rounded-xl border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 font-semibold text-sm hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-all"
                >
                  Generate More
                </button>
              </div>

              <ResetButton onClick={reset}>Start Over</ResetButton>
            </div>
          )}
        </div>
      </div>
    </ToolPageLayout>
  )
}
